import React from 'react';
import { ApiDataBinding, withApiData } from 'react-api-data';
import { ApiResponse, SpeedAndInterfaceType } from 'constants/api';
import { compose } from 'recompose';
import BaseFormModal, { BaseFormModalProps } from './BaseFormModal';
import { WithTranslation, withTranslation } from 'react-i18next';
import { RouteComponentProps, withRouter } from 'react-router';
import { Omit } from 'lodash';
import { FormikActions, Formik } from 'formik';
import { getLinkToSpeedAndInterfaceType } from 'utils/linksUtils';
import { BaseFormModalTemplate, FormModalButtons } from './BaseModalTemplate';
import { Button, FormGroup, Col, Label } from 'reactstrap';
import FormControl from 'components/utils/FormControl';

interface ConnectProps {
    createSpeedAndInterfaceTypeBinding: ApiDataBinding<ApiResponse<SpeedAndInterfaceType>>;
}

type Props = ConnectProps & BaseFormModalProps & WithTranslation & RouteComponentProps;

const enhance = compose<ConnectProps, {}>(
    withApiData(
        {
            createSpeedAndInterfaceTypeBinding: 'postSpeedAndInterfaceType'
        }
    ),
    withTranslation(),
    withRouter
);

type FormValues = Omit<SpeedAndInterfaceType, 'id' | 'uuid'>;

class SpeedAndInterfaceTypeAddModal extends BaseFormModal<Props> {
    componentDidUpdate(prevProps: Readonly<Props>): void {
        this.handleFormSubmission(
            prevProps.createSpeedAndInterfaceTypeBinding.request,
            this.props.createSpeedAndInterfaceTypeBinding.request,
            {
                successMessage: this.props.t('speedAndInterfaceTypeAddModal.successMessage')
            }
        );
    }

    _handleSubmit = async (values: FormValues, actions: FormikActions<FormValues>) => {
        await this.props.createSpeedAndInterfaceTypeBinding.perform(undefined, values);
        actions.setSubmitting(false);
    };

    protected afterSuccessCallback = () => {
        const { createSpeedAndInterfaceTypeBinding, history } = this.props;

        history.push(getLinkToSpeedAndInterfaceType(createSpeedAndInterfaceTypeBinding.data!.data!.uuid));
    }

    render() {
        const { t } = this.props;

        return (
            <>
                <Formik
                    ref={(formik) => (this.formik = formik)}
                    initialValues={{
                        name: '',
                        speed: 0,
                        interface_type: '',
                        fibre: ''
                    } as FormValues}
                    onSubmit={this._handleSubmit}
                >
                    {({ handleSubmit, isSubmitting, errors, touched }) => (
                        <BaseFormModalTemplate
                            isOpen={this.state.isOpen}
                            toggle={isSubmitting ? undefined : this.toggle}
                            onClosed={this.handleModalClose}
                            handleSubmit={handleSubmit}
                            header={t('speedAndInterfaceTypeAddModal.title')}
                            body={
                                <>
                                    <FormGroup row>
                                        <Col md={3}>
                                            <Label htmlFor="name">
                                                {t('speedAndInterfaceType.name')}
                                            </Label>
                                        </Col>
                                        <Col md={9}>
                                            <FormControl
                                                type="text"
                                                name="name"
                                                errors={errors}
                                                touched={touched}
                                            />
                                        </Col>
                                    </FormGroup>

                                    <FormGroup row>
                                        <Col md={3}>
                                            <Label htmlFor="speed">
                                                {t('speedAndInterfaceTypeAddModal.speed')}
                                            </Label>
                                        </Col>
                                        <Col md={9}>
                                            <FormControl
                                                type="number"
                                                name="speed"
                                                errors={errors}
                                                touched={touched}
                                            />
                                        </Col>
                                    </FormGroup>

                                    <FormGroup row>
                                        <Col md={3}>
                                            <Label htmlFor="interface_type">
                                                {t('speedAndInterfaceType.interface_type')}
                                            </Label>
                                        </Col>
                                        <Col md={9}>
                                            <FormControl
                                                type="text"
                                                name="interface_type"
                                                errors={errors}
                                                touched={touched}
                                            />
                                        </Col>
                                    </FormGroup>

                                    <FormGroup row>
                                        <Col md={3}>
                                            <Label htmlFor="fibre">
                                                {t('speedAndInterfaceType.fibre')}
                                            </Label>
                                        </Col>
                                        <Col md={9}>
                                            <FormControl
                                                type="text"
                                                name="fibre"
                                                errors={errors}
                                                touched={touched}
                                            />
                                        </Col>
                                    </FormGroup>
                                </>
                            }
                            footer={<FormModalButtons loading={isSubmitting} toggle={this.toggle} />}
                        />
                    )}
                </Formik>

                <Button color="light" onClick={this.toggle}>
                    {t('speedAndInterfaceTypeAddModal.addButton')}
                </Button>
            </>
        );
    }
}

export default enhance(SpeedAndInterfaceTypeAddModal);
